import { Button, Card, Space, Table, Tag, Typography } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '@/services/api.service';
import EducationPageChrome from './components/EducationPageChrome';
import { useAuthStore } from '@/store/authStore';

type Scheme = {
  id: string; name: string; status: string; session_name: string; grade_name: string;
  subject_name: string; component_count: number; publish_hash?: string | null;
};

export default function AssessmentHubPage() {
  const { t } = useTranslation('education');
  const navigate = useNavigate();
  const permissions = useAuthStore((s) => s.user?.permissions || []);
  const canEnter = permissions.includes('education.assessment.entry') || permissions.includes('education.admin');
  const canViewQuestions = permissions.some((permission) =>
    ['education.question.view', 'education.question.manage', 'education.admin'].includes(permission)
  );
  const query = useQuery({
    queryKey: ['education-assessment-schemes'],
    queryFn: async () => (await api.get<Scheme[]>('/education/assessments/schemes')).data || [],
  });
  return (
    <EducationPageChrome
      title={t('assessmentTitle')}
      description={t('assessmentIntro')}
      extra={<Space>
        {canViewQuestions && <Button onClick={() => navigate('/education/questions')}>{t('questionBank')}</Button>}
        {canEnter && <Button type="primary" onClick={() => navigate('/education/assessments/schemes/new')}>{t('newAssessmentScheme')}</Button>}
      </Space>}
    >
      <Card size="small">
        <Typography.Paragraph type="secondary">{t('assessmentNoFictionNotice')}</Typography.Paragraph>
        <Table
          rowKey="id"
          loading={query.isLoading}
          dataSource={query.data || []}
          columns={[
            { title: t('name'), dataIndex: 'name' },
            { title: t('session'), dataIndex: 'session_name' },
            { title: t('grade'), dataIndex: 'grade_name' },
            { title: t('subject'), dataIndex: 'subject_name' },
            { title: t('components'), dataIndex: 'component_count' },
            { title: t('status'), dataIndex: 'status', render: (status: string) => <Tag>{status}</Tag> },
            {
              title: t('actions'),
              render: (_, row: Scheme) => (
                <Space>
                  <Button size="small" onClick={() => navigate(`/education/assessments/schemes/${row.id}`)}>{t('open')}</Button>
                  <Button size="small" onClick={() => navigate(`/education/assessments/schemes/${row.id}/entry`)}>{t('assessmentEntry')}</Button>
                  <Button size="small" onClick={() => navigate(`/education/assessments/schemes/${row.id}/publish`)}>{t('assessmentPublish')}</Button>
                </Space>
              ),
            },
          ]}
        />
      </Card>
    </EducationPageChrome>
  );
}
